import { useCallback, useEffect, useState } from "react";
import { Plantao } from "../../models/Plantao";
import {
  getShift,
  updateShiftAssistant,
  updateShiftFacilitator,
  updateShiftInterviewer,
  removeShiftInterviewer,
  removeShiftAssistant
} from "../../api/ShiftsApi";
import getMe, { getUsers } from "../../api/UsersApi";
import {
  INTERVIEWER_CAPABILITY_NAMES,
  ASSISTANT_CAPABILITY_NAMES
} from "../../util/permissions";

export type EnrollRole = "interviewer" | "assistant" | "facilitator";
export type EnrollError = "slot_taken" | "api_error" | null;

export type AssignableUser = {
  id: string;
  first_name: string;
  last_name: string;
  cod_curso?: string;
};

export type UseEntrevistasReturn = {
  plantao: Plantao | null;
  loading: boolean;
  enrollError: EnrollError;
  enrollAsInterviewer: (escalaPlantaoId: string) => Promise<void>;
  enrollAsAssistant: (escalaPlantaoId: string) => Promise<void>;
  enrollAsFacilitador: () => Promise<void>;
  assignInterviewerToSlot: (escalaPlantaoId: string, userId: string) => Promise<void>;
  assignAssistantToSlot: (escalaPlantaoId: string, userId: string) => Promise<void>;
  removeInterviewerFromSlot: (escalaPlantaoId: string) => Promise<void>;
  removeAssistantFromSlot: (escalaPlantaoId: string) => Promise<void>;
  /** Returns up to 20 users with interviewer capability matching the query. */
  searchInterviewers: (query: string) => Promise<AssignableUser[]>;
  /** Returns up to 20 users with interviewer OR assistant capability matching the query. */
  searchAssistants: (query: string) => Promise<AssignableUser[]>;
};

const toAssignableUser = (u: any): AssignableUser => ({
  id: u.id,
  first_name: u.first_name ?? "",
  last_name: u.last_name ?? "",
  cod_curso: u.cod_curso
});

export default function useEntrevistas(id: string | null): UseEntrevistasReturn {
  const [plantao, setPlantao] = useState<Plantao | null>(null);
  const [loading, setLoading] = useState(false);
  const [enrollError, setEnrollError] = useState<EnrollError>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    async function fetchPlantao() {
      setLoading(true);
      try {
        const apiResult = await getShift(id!);
        if (!cancelled) setPlantao(apiResult);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchPlantao();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const refreshPlantao = useCallback(async () => {
    if (!id) return;
    const updated = await getShift(id);
    setPlantao(updated);
  }, [id]);

  const enrollAsInterviewer = useCallback(async (escalaPlantaoId: string) => {
    if (!plantao) return;

    const escala = plantao.shift_team_shift.find((e) => e.id === escalaPlantaoId);
    if (!escala) return;

    if (escala.interviewer_user_id !== null) {
      setEnrollError("slot_taken");
      return;
    }

    setEnrollError(null);
    try {
      const me = await getMe();
      await updateShiftInterviewer(escalaPlantaoId, me.id as string);
      await refreshPlantao();
    } catch {
      setEnrollError("api_error");
    }
  }, [plantao, refreshPlantao]);

  const enrollAsAssistant = useCallback(async (escalaPlantaoId: string) => {
    if (!plantao) return;

    const escala = plantao.shift_team_shift.find((e) => e.id === escalaPlantaoId);
    if (!escala) return;

    if (escala.assistant_user_id !== null) {
      setEnrollError("slot_taken");
      return;
    }

    setEnrollError(null);
    try {
      const me = await getMe();
      await updateShiftAssistant(escalaPlantaoId, me.id as string);
      await refreshPlantao();
    } catch {
      setEnrollError("api_error");
    }
  }, [plantao, refreshPlantao]);

  const enrollAsFacilitador = useCallback(async () => {
    if (!plantao) return;

    if (plantao.facilitator_user_id !== null) {
      setEnrollError("slot_taken");
      return;
    }

    setEnrollError(null);
    try {
      const me = await getMe();
      await updateShiftFacilitator(plantao.id, me.id as string);
      await refreshPlantao();
    } catch {
      setEnrollError("api_error");
    }
  }, [plantao, refreshPlantao]);

  const assignInterviewerToSlot = useCallback(async (escalaPlantaoId: string, userId: string) => {
    setEnrollError(null);
    try {
      await updateShiftInterviewer(escalaPlantaoId, userId);
      await refreshPlantao();
    } catch {
      setEnrollError("api_error");
    }
  }, [refreshPlantao]);

  const assignAssistantToSlot = useCallback(async (escalaPlantaoId: string, userId: string) => {
    setEnrollError(null);
    try {
      await updateShiftAssistant(escalaPlantaoId, userId);
      await refreshPlantao();
    } catch {
      setEnrollError("api_error");
    }
  }, [refreshPlantao]);

  const removeInterviewerFromSlot = useCallback(async (escalaPlantaoId: string) => {
    setEnrollError(null);
    try {
      await removeShiftInterviewer(escalaPlantaoId);
      await refreshPlantao();
    } catch {
      setEnrollError("api_error");
    }
  }, [refreshPlantao]);

  const removeAssistantFromSlot = useCallback(async (escalaPlantaoId: string) => {
    setEnrollError(null);
    try {
      await removeShiftAssistant(escalaPlantaoId);
      await refreshPlantao();
    } catch {
      setEnrollError("api_error");
    }
  }, [refreshPlantao]);

  const searchInterviewers = useCallback(async (query: string): Promise<AssignableUser[]> => {
    const users = await getUsers(INTERVIEWER_CAPABILITY_NAMES, query);
    return (users as any[]).map(toAssignableUser);
  }, []);

  const searchAssistants = useCallback(async (query: string): Promise<AssignableUser[]> => {
    const users = await getUsers(ASSISTANT_CAPABILITY_NAMES, query);
    return (users as any[]).map(toAssignableUser);
  }, []);

  return {
    plantao,
    loading,
    enrollError,
    enrollAsInterviewer,
    enrollAsAssistant,
    enrollAsFacilitador,
    assignInterviewerToSlot,
    assignAssistantToSlot,
    removeInterviewerFromSlot,
    removeAssistantFromSlot,
    searchInterviewers,
    searchAssistants
  };
}


